<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceReferences',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    invoice: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const items = computed(() => {
    return props.invoice.references.data;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );

  const act_4044930568678877_emit_5xnf90zye_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4044930568678877_crud_gbnig5nfo_getActionArgs = async ($event) => {
    return {
      objectType: 'invoice_reference',
      op: 'delete',
      data: {
        invoice_id: props.invoice.id,
        etag: props.invoice.etag,
        reference_type: $event.scope.item.object,
        reference_id: $event.scope.item.id,
      },
    };
  };
  const act_4044930568678877_crud_gbnig5nfo_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm deleting the reference.'),
      btnOk: $fn.value.gettext('Delete reference'),
      severity: 'warning',
    };
  };
  const act_8358196963355853_emit_asi06hmuo_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_8358196963355853_close_7xpid6ax1_getWhen = async ($event) => {
    return !!$event.data.modalResult && !!$event.data.modalResult.ok;
  };
  const act_8358196963355853_showModal_1j4luep5y_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InvoiceReferenceSearch',
      props: {
        invoiceId: props.invoice.id,
      },
    };
  };

  const act_4044930568678877_emit_5xnf90zye = new actionTypes.EmitAction({
    actionArgs: act_4044930568678877_emit_5xnf90zye_getActionArgs,
  });
  const inst_evh_4044930568678877_success_mhd1gnkzu =
    new actionTypes.EventHandlerImpl({
      action: act_4044930568678877_emit_5xnf90zye,
      event: 'success',
      displayName: 'emit',
    });
  const act_4044930568678877_crud_gbnig5nfo = new actionTypes.CRUDAction({
    actionArgs: act_4044930568678877_crud_gbnig5nfo_getActionArgs,
    confirm: act_4044930568678877_crud_gbnig5nfo_getConfirm,
    events: [inst_evh_4044930568678877_success_mhd1gnkzu],
  });
  const inst_evh_4044930568678877_click_2mu81n2ww =
    new actionTypes.EventHandlerImpl({
      action: act_4044930568678877_crud_gbnig5nfo,
      event: 'click',
      displayName: 'crud',
    });
  const inst_evh_4044930568678877_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4044930568678877_click_2mu81n2ww],
    event: 'click',
  });
  const act_8358196963355853_emit_asi06hmuo = new actionTypes.EmitAction({
    actionArgs: act_8358196963355853_emit_asi06hmuo_getActionArgs,
  });
  const inst_evh_8358196963355853_close_7xpid6ax1 =
    new actionTypes.EventHandlerImpl({
      action: act_8358196963355853_emit_asi06hmuo,
      event: 'close',
      when: inst_evh_8358196963355853_close_7xpid6ax1_getWhen,
      displayName: 'emit',
    });
  const act_8358196963355853_showModal_1j4luep5y =
    new actionTypes.ShowModalAction({
      actionArgs: act_8358196963355853_showModal_1j4luep5y_getActionArgs,
      events: [inst_evh_8358196963355853_close_7xpid6ax1],
    });
  const inst_evh_8358196963355853_clickPrevent_1jkylfour =
    new actionTypes.EventHandlerImpl({
      action: act_8358196963355853_showModal_1j4luep5y,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_8358196963355853_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8358196963355853_clickPrevent_1jkylfour],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_4044930568678877_success_mhd1gnkzu,
    inst_evh_4044930568678877_click_2mu81n2ww,
    inst_evh_4044930568678877_click,
    inst_evh_8358196963355853_close_7xpid6ax1,
    inst_evh_8358196963355853_clickPrevent_1jkylfour,
    inst_evh_8358196963355853_clickPrevent,
  };

  const evh_4044930568678877_success_mhd1gnkzu = async (event, scope) => {
    return await actions.inst_evh_4044930568678877_success_mhd1gnkzu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4044930568678877_click_2mu81n2ww = async (event, scope) => {
    return await actions.inst_evh_4044930568678877_click_2mu81n2ww.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4044930568678877_click = async (event, scope) => {
    return await actions.inst_evh_4044930568678877_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_close_7xpid6ax1 = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_close_7xpid6ax1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_clickPrevent_1jkylfour = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_clickPrevent_1jkylfour.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="column">
    <div v-if="items && !items.length">
      {{ pgettext('InvoiceReferences', 'No references added.') }}
    </div>
    <ItpBox direction="column" v-if="items && items.length">
      <ItpBox
        :key="item.id"
        class="app-invoice-reference-item border rounded rounded-3 p-2 mb-2 w-100 overflow-hidden"
        v-for="item in items"
      >
        <InvoiceReferenceItem :item="item" class="w-100 overflow-hidden">
        </InvoiceReferenceItem>
        <ItpLink
          icon="fa-times"
          class="ms-2 ms-auto"
          @click="evh_4044930568678877_click($event, { item })"
        >
        </ItpLink>
      </ItpBox>
    </ItpBox>
    <ItpLink
      :icon="$config.addIcon"
      :text="gettext('Add reference')"
      class="ms-auto"
      @click.prevent="evh_8358196963355853_clickPrevent($event, {})"
    >
    </ItpLink>
  </ItpBox>
</template>
